import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
  LARGER_BREAKPOINT_IDS,
} from '@packages/systems/style/breakpoint-ids';
import type {BreakpointsMap, BreakpointID} from '@packages/systems/style/types';
import {
  DEFAULT_VALUES,
  DEFAULT_MEDIA_QUERIES,
} from '@packages/systems/style-legacy/media-queries';
import {reportBugOnce} from '@packages/utilities/reporter';

/*
 * Helper function to get max width for a breakpoint from DEFAULT_MEDIA_QUERIES
 */
const getMaxWidth = (breakpointId: BreakpointID): number => {
  return DEFAULT_MEDIA_QUERIES.find((mq) => mq.key === breakpointId)?.max ?? 0;
};

/*
 * Strings used to describe breakpoints in the UI and co.
 */
const LARGER_BREAKPOINTS_WORDING: BreakpointsMap<{
  label: string;
  description: null | string;
  copy: string;
}> = {
  [BREAKPOINT_ID_XXL]: {
    label: `${DEFAULT_VALUES[BREAKPOINT_ID_XXL]}px`,
    description: null,
    copy: `Styles added here will apply at ${DEFAULT_VALUES[BREAKPOINT_ID_XXL]}px and up.`,
  },
  [BREAKPOINT_ID_XL]: {
    label: `${DEFAULT_VALUES[BREAKPOINT_ID_XL]}px`,
    description: null,
    copy: `Styles added here will apply at ${DEFAULT_VALUES[BREAKPOINT_ID_XL]}px and up, unless they're edited at a larger breakpoint.`,
  },
  [BREAKPOINT_ID_LARGE]: {
    label: `${DEFAULT_VALUES[BREAKPOINT_ID_LARGE]}px`,
    description: null,
    copy: `Styles added here will apply at ${DEFAULT_VALUES[BREAKPOINT_ID_LARGE]}px and up, unless they're edited at a larger breakpoint.`,
  },
  [BREAKPOINT_ID_MAIN]: {
    label: 'Desktop',
    description: 'Base breakpoint',
    copy: "Desktop styles apply at all breakpoints, unless they're edited at a larger or smaller breakpoint. Start your styling here.",
  },
  [BREAKPOINT_ID_MEDIUM]: {
    label: 'Tablet',
    description: `${getMaxWidth(BREAKPOINT_ID_MEDIUM)}px and down`,
    copy: `Styles added here will apply at ${getMaxWidth(BREAKPOINT_ID_MEDIUM)}px and down, unless they're edited at a smaller breakpoint.`,
  },
  [BREAKPOINT_ID_SMALL]: {
    label: 'Mobile (L)',
    description: `${getMaxWidth(BREAKPOINT_ID_SMALL)}px and down`,
    copy: `Styles added here will apply at ${getMaxWidth(BREAKPOINT_ID_SMALL)}px and down, unless they're edited at a smaller breakpoint.`,
  },
  [BREAKPOINT_ID_TINY]: {
    label: 'Mobile',
    description: `${getMaxWidth(BREAKPOINT_ID_TINY)}px and down`,
    copy: `Styles added here will apply at ${getMaxWidth(BREAKPOINT_ID_TINY)}px and down.`,
  },
};

/**
 * Default breakpoints have a "codename", like Desktop, or Tablet. When showing
 * their label, we should show it as it is. However, larger breakpoints do not
 * have a codename, just their minimum width. In this case, we should show the
 * minimum width as the label, and add a "and up" suffix.
 */
export const getBreakpointLabel = (breakpointId: BreakpointID) => {
  if (LARGER_BREAKPOINT_IDS.includes(breakpointId)) {
    return `${getBreakpointWording(breakpointId).label} and up`;
  }

  return getBreakpointWording(breakpointId).label;
};

export const getBreakpointWording = (breakpointId: BreakpointID) => {
  const breakpointWording = LARGER_BREAKPOINTS_WORDING[breakpointId];

  if (!breakpointWording) {
    reportBugOnce(
      'InvalidBreakpointUsed',
      'Could not return a valid breakpoint wording for the given breakpoint ID.',
      {
        breakpointId,
        team: 'design-experience',
        ticket: 'https://webflow.atlassian.net/browse/DESUX-8557',
      }
    );

    return {
      label: 'Unknown breakpoint',
      description: 'This breakpoint is not recognized.',
      copy: 'This breakpoint is not recognized.',
    };
  }

  return breakpointWording;
};
